import { useCallback, useEffect, useState } from 'react';
import { Text, Alert, AlertIcon } from '@chakra-ui/react';

import {
  DateInputWithConfirm,
  UploadActions,
  UploadComplete as UploadCompleteSection,
  Modal,
  getStringFromDate,
  getDateFromString,
  RadioOptionsWithConfirm,
  IQuestion
} from 'shared-components';

import { isValidDate } from '../../../../../utilities/validation';
import { IDate } from '../../../../../types';

interface IProps {
  closeModal: () => void;
  openUploadModal: (
    docKey: string,
    docType: string,
    sponsorType: string,
    sponsorKey: string | null,
    modalTitle: string,
    fileNames: string[],
    metadata?: any
  ) => void;
  docType: string;
  sponsorType: string;
  isComplete: boolean;
  docKey: string;
  fileNames: string[];
  visaStartDate: string | undefined;
  visaEndDate: string | undefined;
}

export const NewStudentVisaVignetteModal = (props: IProps) => {
  const {
    closeModal,
    openUploadModal: initOpenUploadModal,
    fileNames,
    docKey,
    isComplete: isDocumentApproved,
    sponsorType,
    docType,
    visaStartDate: initVisaStartDate,
    visaStartDate: initVisaEndDate
  } = props;

  const title = sponsorType === 'beforeTravel' ? 'New Student Visa Vignette' : 'Stamped Student Visa Vignette';
  const [isReceived, setIsReceived] = useState<boolean | undefined>(undefined);

  const openUploadModal = (metadata: any) => {
    initOpenUploadModal(docKey, docType, sponsorType, null, 'Student Visa Vignette', fileNames, metadata);
  };

  const [isComplete, setIsComplete] = useState(false);

  const [visaStartDate, setVisaStartDate] = useState(getDateFromString(initVisaStartDate));
  const [visaStartDateError, setVisaStartDateError] = useState('');

  const [visaEndDate, setVisaEndDate] = useState(getDateFromString(initVisaEndDate));
  const [visaEndDateError, setVisaEndDateError] = useState('');

  const checkDate = useCallback((date: IDate) => {
    return date.DD?.length && date.MM?.length && date.YYYY?.length === 4;
  }, []);

  useEffect(() => {
    const status = Boolean(
      !visaStartDateError.length && !visaEndDateError.length && checkDate(visaStartDate) && checkDate(visaEndDate)
    );
    setIsComplete(status);
  }, [visaStartDateError, visaEndDateError, checkDate, visaStartDate, visaEndDate]);

  const handleClick = (question: IQuestion) => {
    setIsReceived(question.char === 'Y');
  };

  const validateDate = (dateValue: IDate): string => {
    const { YYYY, DD, MM } = dateValue;

    if (YYYY && DD && MM) {
      const isValid = isValidDate(dateValue);
      if (!isValid) {
        return "Doesn't look like a valid date";
      } else {
        return '';
      }
    }

    return '';
  };

  const returnToSection = () => {
    closeModal();
  };

  const uploadDocument = () => {
    openUploadModal({
      visaStartDate: getStringFromDate(visaStartDate),
      visaEndDate: getStringFromDate(visaEndDate)
    });
  };

  return (
    <Modal id={'newStudentVisaVignetteModal'} show={true} title={title} onClose={closeModal} hideFooter>
      {isDocumentApproved ? (
        <UploadCompleteSection fileNames={fileNames} onUpload={uploadDocument} onContinue={returnToSection} />
      ) : (
        <>
          {sponsorType === 'afterArrival' && (
            <Text>
              We require a photo or scan of the student visa vignette page that was stamped by a UK border officer on
              entry to the UK.
            </Text>
          )}

          {sponsorType === 'beforeTravel' && (
            <Text>
              We require a photo or scan of the student visa vignette page that has been added to your passport as part
              of your new student visa.
            </Text>
          )}

          <RadioOptionsWithConfirm
            key={'haveLetter'}
            value={isReceived}
            label={'Have you received your passport with your new visa inside?'}
            onClick={handleClick}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: isReceived
              },
              {
                char: 'N',
                label: 'Not yet',
                selected: isReceived === false
              }
            ]}
          />

          {isReceived && (
            <>
              <DateInputWithConfirm
                id={`visa-start-date`}
                label={`What is the visa start date?`}
                value={visaStartDate}
                errorText={visaStartDateError}
                onChangeDD={(evt: any) => {
                  setVisaStartDate({
                    ...visaStartDate,
                    DD: evt.target.value
                  });
                }}
                onChangeMM={(evt: any) => {
                  setVisaStartDate({
                    ...visaStartDate,
                    MM: evt.target.value
                  });
                }}
                onChangeYY={(evt: any) => {
                  setVisaStartDate({
                    ...visaStartDate,
                    YYYY: evt.target.value
                  });
                }}
                onBlur={() => setVisaStartDateError(validateDate(visaStartDate))}
              />

              <DateInputWithConfirm
                id={`visa-end-date`}
                label={`What is the visa end date?`}
                value={visaEndDate}
                errorText={visaEndDateError}
                onChangeDD={(evt: any) => {
                  setVisaEndDate({
                    ...visaEndDate,
                    DD: evt.target.value
                  });
                }}
                onChangeMM={(evt: any) => {
                  setVisaEndDate({
                    ...visaEndDate,
                    MM: evt.target.value
                  });
                }}
                onChangeYY={(evt: any) => {
                  setVisaEndDate({
                    ...visaEndDate,
                    YYYY: evt.target.value
                  });
                }}
                onBlur={() => setVisaEndDateError(validateDate(visaEndDate))}
              />
            </>
          )}

          {isReceived === false && (
            <>
              {sponsorType === 'beforeTravel' && (
                <Alert mb={6}>
                  <AlertIcon />
                  If your passport has not yet been returned to you with the visa vignette inside, please come back to
                  this page to upload it when it arrives.
                </Alert>
              )}

              {sponsorType === 'afterArrival' && (
                <Alert mb={6}>
                  <AlertIcon />
                  If you have not yet passed through the UK border, please come back to this page later to upload your
                  stamped visa page.
                </Alert>
              )}
            </>
          )}

          {isReceived !== undefined && (
            <UploadActions
              complete={isReceived}
              disabled={!(!isReceived || (isReceived && isComplete))}
              onContinue={isReceived ? uploadDocument : returnToSection}
            />
          )}
        </>
      )}
    </Modal>
  );
};
